import React, { useEffect, useRef, useState } from 'react';
import loginLogo from '../../assets/gateway-logo-navy.svg';
import splash from '../../assets/gateway-login-left.svg';
import splash2 from '../../assets/gateway-login-right.svg';
import {
    Box,
    Button,
    Link,
    Grid,
    TextField,
    Typography,
    CircularProgress
} from "@mui/material";
import {
    ResetPasswordObject, useResetPasswordMutation, ValidatePasswordTokenObject, useValidatePasswordTokenMutation
} from "../../services/grace";
import { useAlertDispatch } from '../../context/AlertContext/AlertContext';
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"; 
import PasswordValidator from "password-validator";
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

enum PageModes {
    ValidateToken = "ValidateToken",
    InvalidToken = "InvalidToken",
    ResetPassword = "ResetPassword",
    SuccessResetPassword = "SuccessResetPassword",
}
declare type PageMode = PageModes.ValidateToken | PageModes.InvalidToken | PageModes.ResetPassword | PageModes.SuccessResetPassword;

interface ResetPasswordParams {
    token: string;
}
interface ResetPasswordFormValue {
    token: string;
    newPassword: string;
    confirmNewPassword: string;
}
export default function Reset(): JSX.Element {

    const alertDispatch = useAlertDispatch();
    const [pageMode, setPageMode] = useState<PageMode>(PageModes.ValidateToken);
    const [title, setTitle] = useState<string>("Reset your password");
    const [inProgress, setInProgress] = useState(false);

    const { token } = useParams<ResetPasswordParams>();
    
    const passwordRef = useRef<HTMLInputElement>();

    const [resetPassword] = useResetPasswordMutation();
    const [validatePasswordToken] = useValidatePasswordTokenMutation();

    const [formValues, setFormValues] = useState<ResetPasswordFormValue>({
        token: token,
        newPassword: "",
        confirmNewPassword: "",
    });

    useEffect(() => {
        const validateToken = async () => {
            try {
                setInProgress(true);
                const payload: ValidatePasswordTokenObject = {
                    token: formValues.token,
                }
                await validatePasswordToken(payload).unwrap();
                setPageMode(PageModes.ResetPassword);
            } catch (error) {
                setPageMode(PageModes.InvalidToken); 
            }
            setInProgress(false);
        };
    
        validateToken(); 
     
      }, []); 


    const [criteriaFlags, setCriteriaFlags] = useState<any>([]);

    useEffect(() => {
        if (pageMode === PageModes.ResetPassword) {
            setTitle("Reset your password");
            setTimeout(() => {
                passwordRef?.current?.focus();
            }, 50);
        } else if (pageMode === PageModes.InvalidToken) {
            setTitle("Expired or Invalid Password Reset Link");
        } else if (pageMode === PageModes.SuccessResetPassword) {
            setTitle("Password Successfully Reset");
        }  
    }, [pageMode])

    useEffect(() => {
        const schema = new PasswordValidator();

        schema
            .is().min(10)
            .has().symbols()
            .has().digits()
            .has().uppercase()
            .has().lowercase();

        const result = schema.validate(formValues.newPassword, { list: true });
        const errorList = result as any[];

        if (formValues.newPassword === "" || formValues.newPassword !== formValues.confirmNewPassword) {
            errorList.push("match");
        }

        setCriteriaFlags(errorList);
    }, [formValues]);

    const containsOnlyOneMatchOrEmpty = (arr: string[]) :boolean =>  { 
        return (arr.length === 0) || (arr.length === 1 && arr[0] === "match");
    }

    const handleBackToSignInLink = () => alertDispatch({ type: 'redirect', payload: '/' });

    const handleSubmit = async () => {
        if (formValues.token === "" || formValues.newPassword === "" || formValues.confirmNewPassword === "") {
            return;
        }

        if (criteriaFlags.length !== 0) {
            return;
        }

        const payload: ResetPasswordObject = {
            vt: formValues.token,
            newPassword: formValues.newPassword,
        }

        try {
            setInProgress(true);
            await resetPassword(payload).unwrap();
            setPageMode(PageModes.SuccessResetPassword);
            setFormValues({
                token: "",
                newPassword: "",
                confirmNewPassword: "",
            }); 
        } catch (error) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to update password",
                    }
                });
            }
        }

        setInProgress(false);
    };
 
    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.value,
        });
    };
    return (
        <Box sx={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            minHeight: "100vh",
            width: "100%"
        }}> 
            <Box component="img" src={splash} alt="Splash" sx={{
                flex: 1,
                width: "100%",
                maxWidth: "774px",
                minWidth: "250px",
                alignSelf: "flex-start"
            }} />
            <Box sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 1
            }}>
                <img src={loginLogo} alt="Logo" width="280px" />
                <Typography variant="h5">
                    {title}
                </Typography>


                {(pageMode === PageModes.ValidateToken) && (
                    <Box sx={{ margin: 3 }}>
                        <CircularProgress size={50} color={"secondary"} />
                    </Box>
                )}

                {(pageMode === PageModes.InvalidToken) && (
                    <Box sx={{ margin: 3 }}>
                        <p>The password reset link is no longer valid or has expired. Please request a new password reset link to continue.</p>
                        <div>
                            <Link onClick={handleBackToSignInLink} color="primary" underline="none" sx={{ cursor: "pointer" }}>
                                <Typography variant="h5">
                                    Back to Sign in 
                                    <FontAwesomeIcon 
                                        style={{ marginLeft: "10px" }} 
                                        icon={['fas', 'arrow-right']} /> 
                                </Typography>
                            </Link>
                        </div>
                    </Box>
                )}

                {(pageMode === PageModes.ResetPassword) && (
                    <>
                    <TextField
                        inputRef={passwordRef}
                        sx={{ marginBottom: 1 }}
                        variant="outlined"
                        margin="normal"
                        disabled={inProgress}
                        required
                        error={formValues.newPassword.length > 0 && !containsOnlyOneMatchOrEmpty(criteriaFlags)}
                        fullWidth
                        id="password"
                        label="Enter new password"
                        name="newPassword" 
                        onChange={handlePasswordChange}
                        value={formValues.newPassword}
                        type="password"
                        autoFocus
                    />

                    <TextField
                        sx={{ marginBottom: 1 }}
                        fullWidth
                        label="Confirm new password"
                        name="confirmNewPassword"
                        error={formValues.newPassword.length > 0 && criteriaFlags.length > 0}
                        required
                        type='password'
                        value={formValues.confirmNewPassword}
                        onChange={handlePasswordChange}
                    />

                    <Grid container sx={{
                        flex: 1,
                        spacing: 1,
                        marginBottom: 1,
                    }}> 
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ marginLeft: "5px" }}>
                                New passwords must:
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!criteriaFlags.includes("match") &&
                                <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                            }
                            {criteriaFlags.includes("match") &&
                                <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                            }
                            <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                                Match
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!criteriaFlags.includes("min") &&
                                <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                            }
                            {criteriaFlags.includes("min") &&
                                <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                            }
                            <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                                Be at least 10 characters
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!criteriaFlags.includes("symbols") &&
                                <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                            }
                            {criteriaFlags.includes("symbols") &&
                                <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                            }
                            <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                                Contain non-alphanumeric characters (!@#$%^&)
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!criteriaFlags.includes("digits") &&
                                <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                            }
                            {criteriaFlags.includes("digits") &&
                                <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                            }
                            <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                                Contain numeric characters (0123456789)
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!criteriaFlags.includes("uppercase") &&
                                <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                            }
                            {criteriaFlags.includes("uppercase") &&
                                <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                            }
                            <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                                Contain uppercase characters (ABC)
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!criteriaFlags.includes("lowercase") &&
                                <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                            }
                            {criteriaFlags.includes("lowercase") &&
                                <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                            }
                            <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                                Contain lowercase characters (abc)
                            </Typography>
                        </Grid>
                    </Grid>


                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ marginBottom: 1 }}
                        disabled={criteriaFlags.length > 0 || inProgress}
                        onClick={handleSubmit}
                    >
                        Reset
                    </Button>
                    </>
                )}
                
                {(pageMode === PageModes.SuccessResetPassword) && (
                    <Box sx={{ margin: 3 }}>
                        <p>Your password has been successfully reset. You can now log in using your new password</p>
                        <div>
                            <Link onClick={handleBackToSignInLink} color="primary" underline="none" sx={{ cursor: "pointer" }}>
                                <Typography variant="h5">
                                    Back to Sign in 
                                    <FontAwesomeIcon 
                                        style={{ marginLeft: "10px" }} 
                                        icon={['fas', 'arrow-right']} /> 
                                </Typography>
                            </Link>
                        </div>
                    </Box>
                )}
            </Box>
            <Box component="img" src={splash2} alt="Splash" sx={{
                flex: 1,
                width: "100%",
                maxWidth: "774px",
                minWidth: "250px",
                alignSelf: "flex-end"
            }} />
        </Box>
    )
}
